<script setup>
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminCenterStore } from '../../stores/AdminCenterStore'
import { useAuthStore } from '../../stores/AuthStore'
import { PERMISSION_KEYS } from '../../access/permissionKeys'
import { useNavigationStore } from '../../stores/NavigationStore'
import AlertNotification from '../AlertNotification.vue'
import AgencyStatsCards from './AgencyStatsCards.vue'
import ManageUsers from './ManageUsers.vue'
import EmployerAssignments from './EmployerAssignments.vue'
import TransformCertificationProgress from './TransformCertificationProgress.vue'
import Footer from '../FooterSection.vue'

const TRANSFORM_MODULE_KEY = 'transform'

const adminCenterStore = useAdminCenterStore()
const authStore = useAuthStore()
const navigationStore = useNavigationStore()
const { customNavigation } = storeToRefs(navigationStore)
const { moduleKeys } = storeToRefs(authStore)
const { certificationProgressRows } = storeToRefs(adminCenterStore)

const showSuccessAlert = ref(false)
const successAlertMessage = ref('')

/*
 * The certification section is for agencies with the Transform module. An agency without it
 * can still have learners through a role or an explicit grant — then the section shows up
 * once those rows arrive.
 */
const showCertificationProgress = computed(() =>
  moduleKeys.value.includes(TRANSFORM_MODULE_KEY) || certificationProgressRows.value.length > 0
)

function onShowAlert(message) {
  successAlertMessage.value = message
  showSuccessAlert.value = true
}

function setShowSuccessAlert(val) {
  showSuccessAlert.value = val
}

onMounted(() => {
  if (!authStore.hasPermission(PERMISSION_KEYS.ADMIN_CENTER_ACCESS)) {
    customNavigation.value.navigate({ newPage: 'home' })
    return
  }
  adminCenterStore.loadAll()
  adminCenterStore.loadEmployerAssignments()
  adminCenterStore.loadCertificationProgress()
})
</script>

<template>
  <div class="admin-center-wrapper">
    <div class="container-fluid main-container px-0 content-area">
      <div class="bcs-page-padding">
        <!-- Global Success Alert -->
        <div v-if="showSuccessAlert" class="row mt-3">
          <div class="col-12">
            <AlertNotification
              :showAlert="showSuccessAlert"
              @update:showAlert="setShowSuccessAlert"
              type-alert="success"
            >
              <strong>{{ successAlertMessage }}</strong>
            </AlertNotification>
          </div>
        </div>

        <!-- Header -->
        <div class="row mt-5">
          <div class="col-12 mt-3 mb-1">
            <h2 class="text-28 text-secondary mb-0">
              <med>Administrative Center</med>
            </h2>
            <p class="text-16 text-muted mb-0 mt-1">
              Manage account owner assignments and team member access.
            </p>
          </div>
        </div>

        <!-- Stats Cards -->
        <AgencyStatsCards />

        <!-- Manage Users Section -->
        <div class="row mb-4">
          <div class="col-12">
            <ManageUsers @showAlert="onShowAlert" />
          </div>
        </div>

        <!-- Employer Assignments Section -->
        <div class="row mb-4">
          <div class="col-12">
            <EmployerAssignments @showAlert="onShowAlert" />
          </div>
        </div>

        <!-- BCS Transform Certification Progress Section -->
        <div v-if="showCertificationProgress" class="row mb-4">
          <div class="col-12">
            <TransformCertificationProgress />
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<style scoped lang="scss">
.admin-center-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
}

.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
